import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  Activity,
  BadgeCheck,
  Building2,
  Check,
  CircleAlert,
  Clock3,
  FileCheck2,
  FileText,
  KeyRound,
  LoaderCircle,
  LogOut,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UploadCloud,
  UserRoundCheck,
} from "lucide-react";
import { ApiFailure, api } from "./api.js";
import type { MysqlMe, OrganizationBinding, PersonalIdentity } from "./auth-types.js";
import { BrandMark } from "./BrandMark.js";

interface IdentityDocument {
  version: string;
  digest: string;
  url: string;
}

interface IdentityCapabilities {
  status: "AVAILABLE" | "UNAVAILABLE";
  authentication: null | {
    totpRequired: boolean;
    challengeTtlSeconds: number;
    maxChallengeAttempts: number;
    sessionAbsoluteSeconds: number;
    sessionIdleSeconds: number;
  };
  personalIdentity: { status: "AVAILABLE" | "UNAVAILABLE"; document: IdentityDocument | null };
  organizationBinding: { status: "AVAILABLE" | "UNAVAILABLE"; document: IdentityDocument | null };
}

interface SecuritySession {
  id: string;
  current: boolean;
  state: "ACTIVE" | "REVOKED" | "EXPIRED";
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  idleExpiresAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
}

interface SecurityEvent {
  sequence: number;
  id: string;
  action: string;
  objectType: string;
  objectId: string | null;
  requestId: string;
  outcome: string;
  occurredAt: string;
}

type ReviewMaterialType = "IDENTITY_DOCUMENT" | "BUSINESS_LICENSE" | "AUTHORIZATION_LETTER";

interface ReviewMaterial {
  id: string;
  materialType: ReviewMaterialType | "LIVENESS_RESULT" | "PROVIDER_RESULT" | "OTHER";
  fileName: string;
  byteSize: number;
  scannerAssurance: string;
  createdAt: string;
}

interface RevocationRequest {
  id: string;
  subjectType: "PERSONAL" | "ORGANIZATION";
  status: "PENDING_REVIEW" | "COOLING" | "APPROVED" | "REJECTED" | "CANCELLED" | "APPLIED";
  requestedAt: string;
  coolingUntil: string | null;
}

interface RecoveryCodeState {
  configured: boolean;
  status: "ACTIVE" | "REVOKED" | "EXHAUSTED" | "NOT_CONFIGURED";
  remainingCodes: number;
  generatedAt: string | null;
}

function declaredMimeForFile(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.toLowerCase().split(".").at(-1);
  const byExtension: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    txt: "text/plain",
    csv: "text/plain",
    log: "text/plain",
    json: "application/json",
  };
  return byExtension[extension ?? ""] ?? "application/octet-stream";
}

function materialName(type: ReviewMaterial["materialType"]): string {
  const names: Record<ReviewMaterial["materialType"], string> = {
    IDENTITY_DOCUMENT: "身份证明",
    LIVENESS_RESULT: "活体核验结果",
    BUSINESS_LICENSE: "营业执照或登记材料",
    AUTHORIZATION_LETTER: "授权委托材料",
    PROVIDER_RESULT: "服务商核验结果",
    OTHER: "其他材料",
  };
  return names[type];
}

function formatTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function shortId(value: string, head = 7): string {
  return value.length > head * 2 + 1 ? `${value.slice(0, head)}…${value.slice(-head)}` : value;
}

function ErrorNotice({ error }: { error: ApiFailure }): React.JSX.Element {
  return (
    <div className="error-notice" role="alert">
      <CircleAlert size={18} />
      <span><b>{error.message}</b><small>{error.code}{error.requestId ? ` · 请求 ${shortId(error.requestId, 6)}` : ""}</small></span>
    </div>
  );
}

function identityStatus(identity: PersonalIdentity | null): { label: string; className: string } {
  if (!identity) return { label: "未提交", className: "status-draft" };
  if (identity.status === "VERIFIED") return { label: "已核验", className: "status-received" };
  if (identity.status === "REJECTED" || identity.status === "REVOKED") return { label: identity.status === "REJECTED" ? "已驳回" : "已撤销", className: "status-warning" };
  return { label: identity.status === "SUBMITTED" ? "已提交" : "审核中", className: "status-draft" };
}

export function IdentityOnboarding({
  initialMe,
  onProfileChange,
  onLogout,
  onOpenEvidence,
  onOpenReview,
  onOpenSecurity,
}: {
  initialMe: MysqlMe;
  onProfileChange: (me: MysqlMe) => void;
  onLogout: () => void;
  onOpenEvidence: () => void;
  onOpenReview: () => void;
  onOpenSecurity: () => void;
}): React.JSX.Element {
  const [me, setMe] = useState(initialMe);
  const [capabilities, setCapabilities] = useState<IdentityCapabilities | null>(null);
  const [sessions, setSessions] = useState<SecuritySession[]>([]);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [materials, setMaterials] = useState<Record<string, ReviewMaterial[]>>({});
  const [revocations, setRevocations] = useState<RevocationRequest[]>([]);
  const [recoveryState, setRecoveryState] = useState<RecoveryCodeState | null>(null);
  const [recoveryTotp, setRecoveryTotp] = useState("");
  const [revealedRecoveryCodes, setRevealedRecoveryCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [success, setSuccess] = useState("");

  const [realName, setRealName] = useState("");
  const [documentType, setDocumentType] = useState<"PRC_RESIDENT_ID" | "OTHER">("PRC_RESIDENT_ID");
  const [documentNumber, setDocumentNumber] = useState("");
  const [personalConsent, setPersonalConsent] = useState(false);
  const [personalKey, setPersonalKey] = useState(() => crypto.randomUUID());

  const [legalName, setLegalName] = useState("");
  const [creditCode, setCreditCode] = useState("");
  const [authority, setAuthority] = useState<"LEGAL_REPRESENTATIVE" | "AUTHORIZED_AGENT">("LEGAL_REPRESENTATIVE");
  const [intendedUsage, setIntendedUsage] = useState("");
  const [enterpriseConsent, setEnterpriseConsent] = useState(false);
  const [organizationKey, setOrganizationKey] = useState(() => crypto.randomUUID());
  const [appealReason, setAppealReason] = useState("");
  const [revocationTarget, setRevocationTarget] = useState("");
  const [revocationReason, setRevocationReason] = useState("");
  const [stepUpPassword, setStepUpPassword] = useState("");
  const [stepUpTotp, setStepUpTotp] = useState("");

  const replaceProfile = useCallback((profile: MysqlMe): void => {
    setMe(profile);
    onProfileChange(profile);
  }, [onProfileChange]);

  const load = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [profile, capability, sessionList, eventList, revocationList, recoveryCodeState] = await Promise.all([
        api<MysqlMe>("/v2/me"),
        api<IdentityCapabilities>("/v2/identity/capabilities"),
        api<SecuritySession[]>("/v2/security/sessions"),
        api<SecurityEvent[]>("/v2/security/events?limit=20"),
        api<RevocationRequest[]>("/v2/identity/revocation-requests"),
        api<RecoveryCodeState>("/v2/security/mfa/recovery-codes"),
      ]);
      replaceProfile(profile.data);
      setCapabilities(capability.data);
      setSessions(sessionList.data);
      setEvents(eventList.data);
      setRevocations(revocationList.data);
      setRecoveryState(recoveryCodeState.data);
      const caseIds = [
        profile.data.personalIdentity?.reviewCaseId,
        ...profile.data.organizations.map((organization) => organization.application?.reviewCaseId),
      ].filter((value): value is string => Boolean(value));
      const materialEntries = await Promise.all(caseIds.map(async (caseId) => {
        const response = await api<ReviewMaterial[]>(`/v2/identity/review-cases/${caseId}/materials`);
        return [caseId, response.data] as const;
      }));
      setMaterials(Object.fromEntries(materialEntries));
    } catch (cause) {
      const failure = cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法加载身份安全中心。");
      if (failure.status === 401) onLogout();
      else setError(failure);
    } finally {
      setLoading(false);
    }
  }, [onLogout, replaceProfile]);

  useEffect(() => { void load(); }, [load]);

  const submitPersonal = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const document = capabilities?.personalIdentity.document;
    if (!document || !personalConsent) return;
    setBusy("personal");
    setError(null);
    setSuccess("");
    try {
      await api<PersonalIdentity>("/v2/personal-identity", {
        method: "POST",
        headers: { "idempotency-key": personalKey },
        body: JSON.stringify({
          realName,
          documentType,
          documentNumber,
          acceptSensitiveInfo: true,
          sensitiveInfoVersion: document.version,
        }),
      });
      setDocumentNumber("");
      setPersonalKey(crypto.randomUUID());
      setSuccess("个人实名认证资料已加密提交，当前状态为待人工审核；尚未宣称认证通过。");
      await load(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交实名认证资料。"));
    } finally {
      setBusy(null);
    }
  };

  const submitOrganization = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const document = capabilities?.organizationBinding.document;
    if (!document || !enterpriseConsent) return;
    setBusy("organization");
    setError(null);
    setSuccess("");
    try {
      await api<OrganizationBinding>("/v2/organizations", {
        method: "POST",
        headers: { "idempotency-key": organizationKey },
        body: JSON.stringify({
          legalName,
          unifiedSocialCreditCode: creditCode,
          applicantAuthority: authority,
          intendedUsage,
          acceptEnterpriseAuthorization: true,
          enterpriseAuthorizationVersion: document.version,
        }),
      });
      setLegalName("");
      setCreditCode("");
      setIntendedUsage("");
      setEnterpriseConsent(false);
      setOrganizationKey(crypto.randomUUID());
      setSuccess("企业认证申请已提交。一个账号可以继续申请或受邀绑定其他企业，但审核通过前不获得企业业务权限。");
      await load(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交企业认证申请。"));
    } finally {
      setBusy(null);
    }
  };

  const uploadMaterial = async (caseId: string, materialType: ReviewMaterialType, file: File | undefined): Promise<void> => {
    if (!file) return;
    const busyKey = `material:${caseId}:${materialType}`;
    setBusy(busyKey);
    setError(null);
    setSuccess("");
    try {
      await api<ReviewMaterial>(`/v2/identity/review-cases/${caseId}/materials`, {
        method: "POST",
        headers: {
          "content-type": "application/octet-stream",
          "x-lmx-file-name": encodeURIComponent(file.name),
          "x-lmx-material-type": materialType,
          "x-lmx-declared-mime": declaredMimeForFile(file),
        },
        body: file,
      });
      setSuccess(`${materialName(materialType)}已通过本地内容策略扫描并以每对象密钥加密保存；审核员调阅会留下追加式审计记录。`);
      await load(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法上传认证材料。"));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (sessionId: string): Promise<void> => {
    setBusy(`session:${sessionId}`);
    setError(null);
    try {
      await api<void>(`/v2/security/sessions/${sessionId}`, { method: "DELETE" });
      await load(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法撤销会话。"));
    } finally {
      setBusy(null);
    }
  };

  const switchScope = async (scopeType: "ONBOARDING" | "PERSONAL" | "ORGANIZATION", targetId?: string): Promise<void> => {
    setBusy(`scope:${targetId ?? scopeType}`);
    setError(null);
    try {
      const response = await api<MysqlMe>("/v2/auth/session/scope", {
        method: "POST",
        body: JSON.stringify({ scopeType, targetId }),
      });
      replaceProfile(response.data);
      setSuccess("身份上下文已切换，会话令牌和 CSRF 凭证已同步轮换。");
      await load(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法切换身份上下文。"));
    } finally {
      setBusy(null);
    }
  };

  const submitAppeal = async (caseId: string): Promise<void> => {
    if (appealReason.trim().length < 10) return;
    setBusy(`appeal:${caseId}`); setError(null); setSuccess("");
    try {
      await api(`/v2/identity/review-cases/${caseId}/appeals`, { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify({ reason: appealReason }) });
      setAppealReason(""); setSuccess("申诉已提交并进入独立申诉队列；原初审员和原复核员不能处理。");
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交认证申诉。")); }
    finally { setBusy(null); }
  };

  const requestRevocation = async (): Promise<void> => {
    const [subjectType, subjectId] = revocationTarget.split(":");
    if (!subjectId || revocationReason.trim().length < 2 || !stepUpPassword || !/^\d{6}$/u.test(stepUpTotp)) return;
    setBusy("self-revocation"); setError(null); setSuccess("");
    try {
      await api("/v2/identity/revocation-requests", { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify({ subjectType, subjectId, password: stepUpPassword, totpCode: stepUpTotp, reason: revocationReason }) });
      setStepUpPassword(""); setStepUpTotp(""); setRevocationReason("");
      setSuccess("主动撤销申请已进入 24 小时冷静期；生效前可以取消，历史存证和链上事实不会删除。");
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法发起身份撤销。")); }
    finally { setBusy(null); }
  };

  const cancelRevocation = async (id: string): Promise<void> => {
    setBusy(`cancel-revocation:${id}`); setError(null); setSuccess("");
    try { await api(`/v2/identity/revocation-requests/${id}/cancel`, { method: "POST" }); setSuccess("主动撤销已在生效前取消。"); await load(true); }
    catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法取消身份撤销。")); }
    finally { setBusy(null); }
  };

  const generateRecoveryCodes = async (): Promise<void> => {
    if (!/^\d{6}$/u.test(recoveryTotp)) return;
    setBusy("recovery-codes"); setError(null); setSuccess("");
    try {
      const response = await api<RecoveryCodeState>("/v2/security/mfa/recovery-codes", {
        method: "POST", body: JSON.stringify({ totpCode: recoveryTotp }),
      });
      const codes = (response.meta as { recoveryCodes?: string[] } | undefined)?.recoveryCodes ?? [];
      setRecoveryState(response.data); setRevealedRecoveryCodes(codes); setRecoveryTotp("");
      setSuccess("新的 10 个一次性恢复码已经生成；旧恢复码组已原子撤销。请立即离线保存，本页面刷新后不再显示明文。");
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法生成 MFA 恢复码。")); }
    finally { setBusy(null); }
  };

  const logout = async (): Promise<void> => {
    setBusy("logout");
    try {
      await api<void>("/v2/auth/session", { method: "DELETE" });
    } finally {
      onLogout();
    }
  };

  const status = identityStatus(me.personalIdentity);
  const personalDocument = capabilities?.personalIdentity.document;
  const organizationDocument = capabilities?.organizationBinding.document;

  return (
    <div className="workspace onboarding-workspace">
      <header className="workspace-header">
        <div className="brand onboarding-brand"><BrandMark /><span><b>链脉信枢</b><small>LIANMAI TRUST HUB</small></span></div>
        <div className="workspace-actions">
          <span className="environment"><ShieldCheck />MySQL 身份域 · MFA 已验证</span>
          {me.trustedContext && <button className="secondary-button" onClick={onOpenEvidence}><FileCheck2 />存证工作台</button>}
          {me.capabilities.reviewIdentities && <button className="secondary-button" onClick={onOpenReview}><BadgeCheck />审核工作台</button>}
          {me.capabilities.viewPlatformSecurity && <button className="secondary-button" onClick={onOpenSecurity}><ShieldCheck />平台安全</button>}
          <button className="secondary-button" disabled={loading} onClick={() => void load()}>{loading ? <LoaderCircle className="spin" /> : <RefreshCw />}刷新</button>
          <button className="icon-button" aria-label="退出登录" disabled={busy === "logout"} onClick={() => void logout()}><LogOut /></button>
        </div>
      </header>

      <main className="workspace-main onboarding-main">
        <section className="welcome-band onboarding-welcome">
          <div><span className="eyebrow"><BadgeCheck />首次登录安全闭环已完成</span><h1>可信身份开户注册中心</h1><p>{me.onboarding.message}</p></div>
          <div className="onboarding-account"><small>当前账号</small><b>{me.maskedEmail}</b><code>{shortId(me.accountId)}</code></div>
        </section>

        <section className={`boundary-banner ${me.onboarding.trustedBusinessAccess ? "ready-boundary" : ""}`}>
          <span className="boundary-icon">{me.onboarding.trustedBusinessAccess ? <BadgeCheck /> : <Clock3 />}</span>
          <div><b>{me.onboarding.trustedBusinessAccess ? "可信身份上下文已生效" : "业务权限仍处于 ONBOARDING 隔离区"}</b><p>{me.onboarding.trustedBusinessAccess ? "可切换到已核验的个人或企业身份。" : "当前只能维护账户安全和提交身份材料；审核通过前不能写入可信存证、数字资产或联盟治理数据。"}</p></div>
          <span className="truth-label">{me.session.scopeType}</span>
        </section>

        {error && <ErrorNotice error={error} />}
        {success && <div className="success-notice"><Check /><span><b>操作已受理</b><small>{success}</small></span></div>}

        <section className="identity-security-grid">
          <article><span><KeyRound /></span><div><small>密码登录</small><strong>已通过</strong><em>MySQL 全局账号</em></div></article>
          <article><span className="green"><ShieldCheck /></span><div><small>第二重验证</small><strong>TOTP 已激活</strong><em>同一时间步禁止重放</em></div></article>
          <article><span className="violet"><Clock3 /></span><div><small>会话空闲到期</small><strong>{formatTime(me.session.idleExpiresAt)}</strong><em>绝对到期 {formatTime(me.session.expiresAt)}</em></div></article>
        </section>

        <section className="onboarding-columns">
          <article className="panel identity-onboarding-panel">
            <div className="panel-header"><div><span className="heading-icon"><UserRoundCheck /></span><span><h2>个人实名认证</h2><p>一个账号最多绑定一个自然人</p></span></div><span className={`status ${status.className}`}>{status.label}</span></div>
            {me.personalIdentity ? (
              <>
                <div className="submitted-identity-card">
                  <UserRoundCheck />
                  <span><b>{me.personalIdentity.maskedRealName}</b><small>{me.personalIdentity.maskedDocumentNumber} · {me.personalIdentity.verificationMethod === "MANUAL_REVIEW" ? "人工审核" : me.personalIdentity.verificationProvider}</small></span>
                  <time>{formatTime(me.personalIdentity.submittedAt)}</time>
                </div>
                {me.personalIdentity.reviewCaseId && ["SUBMITTED", "UNDER_REVIEW"].includes(me.personalIdentity.status) && (
                  <div className="material-uploader">
                    <div><FileText /><span><b>受控认证材料</b><small>必需：身份证明。原文不上链，扫描后以 AES-256-GCM 密文保存。</small></span></div>
                    <label className="file-button">
                      {busy === `material:${me.personalIdentity.reviewCaseId}:IDENTITY_DOCUMENT` ? <LoaderCircle className="spin" /> : <UploadCloud />}
                      上传身份证明
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={Boolean(busy)} onChange={(event) => void uploadMaterial(me.personalIdentity!.reviewCaseId!, "IDENTITY_DOCUMENT", event.target.files?.[0])} />
                    </label>
                    <div className="material-list">
                      {(materials[me.personalIdentity.reviewCaseId] ?? []).map((material) => <span key={material.id}><Check />{materialName(material.materialType)} · {material.fileName} · {(material.byteSize / 1024).toFixed(1)} KiB</span>)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <form className="onboarding-form" onSubmit={(event) => void submitPersonal(event)}>
                <label>真实姓名<input autoComplete="name" minLength={2} maxLength={80} value={realName} onChange={(event) => setRealName(event.target.value)} required /></label>
                <label>证件类型<select value={documentType} onChange={(event) => setDocumentType(event.target.value as "PRC_RESIDENT_ID" | "OTHER")}><option value="PRC_RESIDENT_ID">中华人民共和国居民身份证</option><option value="OTHER">其他有效证件</option></select></label>
                <label>证件号码<input autoComplete="off" minLength={5} maxLength={64} value={documentNumber} onChange={(event) => setDocumentNumber(event.target.value.toUpperCase().trim())} required /></label>
                <label className="check-row"><input type="checkbox" checked={personalConsent} onChange={(event) => setPersonalConsent(event.target.checked)} required /><span>我已阅读并同意 {personalDocument ? <a href={personalDocument.url} target="_blank" rel="noreferrer">《个人敏感信息处理规则》</a> : "《个人敏感信息处理规则》"}{personalDocument ? `（${personalDocument.version}）` : "（尚未配置）"}</span></label>
                <div className="warning-notice compact-warning"><CircleAlert /><span><b>提交不等于认证通过</b><small>姓名和证件号会加密保存，列表仅展示脱敏信息；当前交付进入人工审核队列。</small></span></div>
                <button className="primary-button" disabled={busy === "personal" || !personalConsent || !personalDocument}>{busy === "personal" ? <LoaderCircle className="spin" /> : <UserRoundCheck />}加密提交实名认证</button>
              </form>
            )}
          </article>

          <article className="panel identity-onboarding-panel">
            <div className="panel-header"><div><span className="heading-icon"><Building2 /></span><span><h2>企业认证与绑定</h2><p>一个账号可以绑定多个企业</p></span></div><span className="record-count">{me.organizations.length} 个</span></div>
            <form className="onboarding-form" onSubmit={(event) => void submitOrganization(event)}>
              <label>企业法定名称<input minLength={2} maxLength={190} value={legalName} onChange={(event) => setLegalName(event.target.value)} required /></label>
              <label>统一社会信用代码<input className="mono-input" minLength={18} maxLength={18} value={creditCode} onChange={(event) => setCreditCode(event.target.value.toUpperCase().trim())} required /></label>
              <label>申请人身份<select value={authority} onChange={(event) => setAuthority(event.target.value as typeof authority)}><option value="LEGAL_REPRESENTATIVE">法定代表人</option><option value="AUTHORIZED_AGENT">授权经办人</option></select></label>
              <label>拟使用场景<textarea minLength={10} maxLength={1000} value={intendedUsage} onChange={(event) => setIntendedUsage(event.target.value)} placeholder="例如：合同存证、数据审计与可信验证" required /></label>
              <label className="check-row"><input type="checkbox" checked={enterpriseConsent} onChange={(event) => setEnterpriseConsent(event.target.checked)} required /><span>我确认有权提交企业资料，并同意 {organizationDocument ? <a href={organizationDocument.url} target="_blank" rel="noreferrer">《企业认证授权书》</a> : "《企业认证授权书》"}{organizationDocument ? `（${organizationDocument.version}）` : "（尚未配置）"}</span></label>
              <button className="primary-button" disabled={busy === "organization" || !enterpriseConsent || !organizationDocument}>{busy === "organization" ? <LoaderCircle className="spin" /> : <Building2 />}提交企业认证申请</button>
            </form>
          </article>
        </section>

        <section className="panel organization-bindings-panel">
          <div className="panel-header"><div><span className="heading-icon"><Building2 /></span><span><h2>我的企业</h2><p>申请和成员关系按企业分别保存，不限制账号绑定企业数量</p></span></div></div>
          {me.organizations.length === 0 ? <div className="empty compact-empty"><Building2 /><p>还没有企业认证申请或有效成员关系。</p></div> : (
            <div className="organization-binding-list">
              {me.organizations.map((organization) => (
                <article key={organization.organizationId}>
                  <div className="organization-binding-row">
                    <Building2 />
                    <span><b>{organization.legalName}</b><small>{organization.application ? `${organization.application.applicationNo} · ${organization.application.status}` : `成员关系 · ${organization.membershipStatus}`}</small></span>
                    <em>{organization.organizationStatus}</em>
                    {organization.membershipStatus === "ACTIVE" && organization.tenantId && <button className="text-button compact" disabled={Boolean(busy)} onClick={() => void switchScope("ORGANIZATION", organization.organizationId)}>切换企业</button>}
                  </div>
                  {organization.application?.reviewCaseId && ["SUBMITTED", "UNDER_REVIEW"].includes(organization.application.status) && (
                    <div className="organization-materials">
                      <span><FileText />必需材料加密上传</span>
                      <label className="file-button compact-file"><UploadCloud />营业执照<input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={Boolean(busy)} onChange={(event) => void uploadMaterial(organization.application!.reviewCaseId!, "BUSINESS_LICENSE", event.target.files?.[0])} /></label>
                      {organization.application.applicantAuthority === "AUTHORIZED_AGENT" && <label className="file-button compact-file"><UploadCloud />授权委托书<input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={Boolean(busy)} onChange={(event) => void uploadMaterial(organization.application!.reviewCaseId!, "AUTHORIZATION_LETTER", event.target.files?.[0])} /></label>}
                      <small>{(materials[organization.application.reviewCaseId] ?? []).map((material) => materialName(material.materialType)).join("、") || "尚未上传"}</small>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
          {me.personalIdentity?.status === "VERIFIED" && <button className="secondary-button" disabled={Boolean(busy)} onClick={() => void switchScope("PERSONAL", me.personalIdentity!.id)}>切换个人身份</button>}
        </section>

        <section className="panel organization-bindings-panel">
          <div className="panel-header"><div><span className="heading-icon"><ShieldCheck /></span><span><h2>身份申诉与主动撤销</h2><p>申诉不直接恢复认证；主动撤销需密码与 TOTP 二次验证并保留 24 小时冷静期。</p></span></div></div>
          {me.personalIdentity?.status === "REJECTED" && me.personalIdentity.reviewCaseId && <div className="onboarding-form"><label>个人认证申诉说明<textarea minLength={10} maxLength={2000} value={appealReason} onChange={(event) => setAppealReason(event.target.value)} placeholder="说明异议事实及新提交的核验材料" /></label><button className="secondary-button" disabled={Boolean(busy) || appealReason.trim().length < 10} onClick={() => void submitAppeal(me.personalIdentity!.reviewCaseId!)}>提交个人认证申诉</button></div>}
          {me.organizations.filter((item) => item.application?.status === "REJECTED" && item.application.reviewCaseId).map((organization) => <div className="onboarding-form" key={`appeal-${organization.organizationId}`}><b>{organization.legalName} · 认证已驳回</b><label>企业认证申诉说明<textarea minLength={10} maxLength={2000} value={appealReason} onChange={(event) => setAppealReason(event.target.value)} /></label><button className="secondary-button" disabled={Boolean(busy) || appealReason.trim().length < 10} onClick={() => void submitAppeal(organization.application!.reviewCaseId!)}>提交企业认证申诉</button></div>)}
          {(me.personalIdentity?.status === "VERIFIED" || me.organizations.some((item) => item.organizationStatus === "VERIFIED" && item.roles.includes("ORG_ADMIN"))) && <div className="onboarding-form"><div className="warning-notice"><CircleAlert /><span><b>撤销后将立即失去对应业务写权</b><small>冷静期届满后，身份、Tenant、成员写权与该身份范围的活跃会话会在同一事务回收。</small></span></div><label>撤销主体<select value={revocationTarget} onChange={(event) => setRevocationTarget(event.target.value)}><option value="">请选择</option>{me.personalIdentity?.status === "VERIFIED" && <option value={`PERSONAL:${me.personalIdentity.id}`}>个人可信身份</option>}{me.organizations.filter((item) => item.organizationStatus === "VERIFIED" && item.roles.includes("ORG_ADMIN")).map((item) => <option value={`ORGANIZATION:${item.organizationId}`} key={item.organizationId}>{item.legalName}</option>)}</select></label><label>撤销原因<textarea minLength={2} maxLength={1000} value={revocationReason} onChange={(event) => setRevocationReason(event.target.value)} /></label><label>当前密码<input type="password" autoComplete="current-password" value={stepUpPassword} onChange={(event) => setStepUpPassword(event.target.value)} /></label><label>6 位 TOTP<input inputMode="numeric" maxLength={6} value={stepUpTotp} onChange={(event) => setStepUpTotp(event.target.value.replace(/\D/gu, ""))} /></label><button className="secondary-button danger-button" disabled={Boolean(busy) || !revocationTarget || !stepUpPassword || !/^\d{6}$/u.test(stepUpTotp) || revocationReason.trim().length < 2} onClick={() => void requestRevocation()}>{busy === "self-revocation" ? <LoaderCircle className="spin" /> : <Trash2 />}进入 24 小时撤销冷静期</button></div>}
          {revocations.length > 0 && <div className="security-session-list">{revocations.map((item) => <div key={item.id}><span className={`session-state ${item.status.toLowerCase()}`}><i />{item.status}</span><span><b>{item.subjectType === "PERSONAL" ? "个人身份撤销" : "企业身份撤销"}</b><small>申请 {formatTime(item.requestedAt)} · 冷静期至 {formatTime(item.coolingUntil)}</small></span>{item.status === "COOLING" && <button className="secondary-button" disabled={Boolean(busy)} onClick={() => void cancelRevocation(item.id)}>取消撤销</button>}</div>)}</div>}
        </section>

        <section className="onboarding-columns security-columns">
          <article className="panel identity-onboarding-panel">
            <div className="panel-header"><div><span className="heading-icon"><KeyRound /></span><span><h2>MFA 一次性恢复码</h2><p>固定 10 个高熵码，数据库只保存域分离 HMAC</p></span></div><span className={`status ${recoveryState?.configured ? "status-received" : "status-warning"}`}>{recoveryState?.configured ? `剩余 ${recoveryState.remainingCodes}` : "未配置"}</span></div>
            <div className="onboarding-form"><div className="warning-notice"><CircleAlert /><span><b>生成新组会立即撤销旧组</b><small>需要输入当前 TOTP 完成最近 5 分钟内的升级验证；每个恢复码只能使用一次。</small></span></div><label>当前 6 位 TOTP<input inputMode="numeric" maxLength={6} value={recoveryTotp} onChange={(event) => setRecoveryTotp(event.target.value.replace(/\D/gu, ""))} /></label><button className="secondary-button" disabled={Boolean(busy) || !/^\d{6}$/u.test(recoveryTotp)} onClick={() => void generateRecoveryCodes()}>{busy === "recovery-codes" ? <LoaderCircle className="spin" /> : <KeyRound />}{recoveryState?.configured ? "轮换恢复码" : "生成恢复码"}</button></div>
            {revealedRecoveryCodes.length > 0 && <div className="recovery-code-grid">{revealedRecoveryCodes.map((item) => <code key={item}>{item}</code>)}</div>}
            {recoveryState?.generatedAt && <small>最近生成：{formatTime(recoveryState.generatedAt)}。明文只在本次响应中出现。</small>}
          </article>
          <article className="panel identity-onboarding-panel">
            <div className="panel-header"><div><span className="heading-icon"><ShieldCheck /></span><span><h2>登录会话</h2><p>服务端同时执行空闲和绝对超时</p></span></div></div>
            <div className="security-session-list">
              {sessions.map((session) => (
                <div key={session.id}>
                  <span className={`session-state ${session.state.toLowerCase()}`}><i />{session.current ? "当前会话" : session.state}</span>
                  <span><code>{shortId(session.id)}</code><small>最近活动 {formatTime(session.lastSeenAt)} · 创建 {formatTime(session.createdAt)}</small></span>
                  {!session.current && session.state === "ACTIVE" && <button className="icon-button danger-button" aria-label="撤销该会话" disabled={busy === `session:${session.id}`} onClick={() => void revoke(session.id)}>{busy === `session:${session.id}` ? <LoaderCircle className="spin" /> : <Trash2 />}</button>}
                </div>
              ))}
            </div>
          </article>

          <article className="panel identity-onboarding-panel">
            <div className="panel-header"><div><span className="heading-icon"><Activity /></span><span><h2>账户安全审计</h2><p>只展示当前账号事件；底层为追加式哈希链</p></span></div></div>
            <div className="security-event-list">
              {events.slice(0, 10).map((item) => <div key={item.id}><i /><span><b>{item.action}</b><small>{item.outcome} · 请求 {shortId(item.requestId, 5)}</small></span><time>{formatTime(item.occurredAt)}</time></div>)}
              {events.length === 0 && <div className="empty compact-empty"><Activity /><p>尚无可显示的安全事件。</p></div>}
            </div>
          </article>
        </section>

        <footer className="workspace-footer"><span>链脉信枢 · 身份运营与生产安全门禁 v0.7 D09＋D10</span><span>lianmai.net.cn · 京ICP备2026024994号-1</span></footer>
      </main>
    </div>
  );
}
